import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Grid, Box, Divider, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import { StatusHistory, StepResult } from '../../../developerModules/model';

import axios from '../../../sharedModules/utils/axios.utils';

type NonSuccessOutcome = 'failed' | 'skipped';

type NonSuccessStepDisplayRow = {
  label: string;
  chipColor: 'error' | 'default';
  captionColor: 'error' | 'text.secondary';
};

const NON_SUCCESS_STEP_DISPLAY: Record<NonSuccessOutcome, NonSuccessStepDisplayRow> = {
  failed: { label: 'Failed', chipColor: 'error', captionColor: 'error' },
  skipped: { label: 'Skipped', chipColor: 'default', captionColor: 'text.secondary' },
};

function getStepStatusRow(
  value: string | StepResult,
): { outcome: 'success' } | { outcome: NonSuccessOutcome; detail?: string } {
  if (typeof value === 'object' && value.status === 'failed') {
    return { outcome: 'failed', detail: value.error };
  }
  if (typeof value === 'object' && value.status === 'skipped') {
    return { outcome: 'skipped', detail: value.reason };
  }
  return { outcome: 'success' };
}

function StepStatusDisplay({ value }: { value: string | StepResult }) {
  const row = getStepStatusRow(value);
  if (row.outcome === 'success') {
    return <Chip label="Success" color="success" size="small" />;
  }
  const { label, chipColor, captionColor } = NON_SUCCESS_STEP_DISPLAY[row.outcome];
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Chip label={label} color={chipColor} size="small" />
      {row.detail && (
        <Typography variant="caption" color={captionColor}>
          {row.detail}
        </Typography>
      )}
    </Box>
  );
}

const ApprovalAutomationsSection: React.FC<{ backflowJobGroupId: string }> = ({ backflowJobGroupId }) => {
  const [history, setHistory] = useState<StatusHistory | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchHistory = async () => {
    try {
      const response = await axios.get<any>('/status_callback_audits', {
        params: {
          'ransack[record_type_eq]': 'BackflowJobGroup',
          'ransack[record_id_eq]': backflowJobGroupId,
          'ransack[new_status_eq]': 'approved',
          per_page: 1,
        },
      });
      const histories = response.data.result ?? [];
      setHistory(histories.length > 0 ? histories[0] : null);
    } catch {
      setHistory(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [backflowJobGroupId]);

  if (loading) return null;
  if (!history) return null;

  const stepEntries = Object.entries(history.step_results_titleized);

  return (
    <Grid item xs={6}>
      <Card elevation={3} sx={{ height: '100%' }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h6" fontWeight="600">
              Approval Automations
            </Typography>
            {history.success === true && (
              <Chip label="All Steps Passed" color="success" size="small" icon={<CheckCircleIcon />} />
            )}
            {history.success === false && (
              <Chip label="Some Steps Failed" color="error" size="small" icon={<ErrorIcon />} />
            )}
            {history.success === null && (
              <Chip label="In Progress" color="warning" size="small" icon={<HourglassEmptyIcon />} />
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          {stepEntries.map(([stepName, value]) => (
            <Box
              key={stepName}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ py: 1, borderBottom: '1px solid #eee' }}
            >
              <Typography variant="body2">{stepName}</Typography>
              <StepStatusDisplay value={value} />
            </Box>
          ))}

          {stepEntries.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No steps recorded yet.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default ApprovalAutomationsSection;
